import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  HomeIcon,
  ArrowRightOnRectangleIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  InformationCircleIcon,
  PresentationChartLineIcon,
  GlobeAltIcon,
  CalendarDaysIcon,
  QueueListIcon,
  Cog8ToothIcon,
  UserIcon
} from '@heroicons/react/24/outline';

export default function Layout() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const sidebarRef = useRef(null);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const lUser = JSON.parse(localStorage.getItem("user"));
  const currentUser = {
    fullName: `${lUser.firstName} ${lUser.lastName} (${lUser.systemId})`,
    role: `${lUser.positionTitle} ${lUser.phoneNumber || ' '}`,
    systemRole: lUser.systemRole
  };

  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
  };

  const menuItems = [
    { to: "/dashboard", label: t('dashboardTitle'), icon: HomeIcon, required: ["COMPANY_MANAGER", "SUPER_ADMIN"] },
    { to: "/profile", label: t('profile'), icon: UserIcon, required: ["COMPANY_EMPLOYEE", "COMPANY_MANAGER", "SUPER_ADMIN"] },
    { to: "/daily_report", label: t('dailySummary'), icon: PresentationChartLineIcon, required: ["COMPANY_MANAGER", "SUPER_ADMIN"] },
    { to: "/monthly_detail", label: t('monthlyDetail'), icon: CalendarDaysIcon, required: ["COMPANY_MANAGER", "SUPER_ADMIN"] },
    { to: "/associates_report", label: t('associateMetrics'), icon: UsersIcon, required: ["COMPANY_MANAGER", "SUPER_ADMIN"] },
    { to: "/events", label: t('checkInOutHistory'), icon: QueueListIcon, required: ["COMPANY_MANAGER", "SUPER_ADMIN"] },
    { to: "/charges", label: t('chargeStrategies'), icon: ExclamationTriangleIcon, required: ["COMPANY_MANAGER", "SUPER_ADMIN"] },
    { to: "/company", label: t('company'), icon: BuildingOfficeIcon, required: ["COMPANY_MANAGER", "SUPER_ADMIN"] },
    { to: "/companies", label: t('companies'), icon: Cog8ToothIcon, required: ["SUPER_ADMIN"] },
    { to: "/users", label: t('users'), icon: UserGroupIcon, required: ["COMPANY_MANAGER", "SUPER_ADMIN"] },
    // { to: "/about", label: t('about'), icon: InformationCircleIcon },
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const changeLanguage = (e) => {
    i18n.changeLanguage(e.target.value);
    localStorage.setItem("language", e.target.value);
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsCollapsed(true);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsCollapsed(true);
  }, [location]);

  // Route protection
  useEffect(() => {
    // Find the current item in menuItems
    const currentItem = menuItems.find(item => item.to === location.pathname);

    if (currentItem && currentItem.required) {
      const isAuthorized = currentItem.required.includes(currentUser.systemRole);
      if (!isAuthorized) {
        // Redirect to safe page
        if (currentUser.systemRole === 'COMPANY_EMPLOYEE') {
          navigate('/profile', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }
    }

    // Explicit check for Employee on Dashboard (which might be the default)
    if (currentUser.systemRole === 'COMPANY_EMPLOYEE' && location.pathname === '/dashboard') {
      navigate('/profile', { replace: true });
    }
  }, [location.pathname, currentUser.systemRole, navigate, menuItems]); // menuItems is stable within the component logic here

  const formatDateTime = (date) => {
    return date.toLocaleString(i18n.language, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };


  return (
    <div className="flex h-dvh w-dvw overflow-hidden bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-gray-100">

      <aside
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 z-[9999] ${isCollapsed ? "w-16 sm:w-20" : "w-64 sm:w-72"
          } bg-gray-900/95 backdrop-blur-md border-r border-gray-800 transition-[width] duration-300 ease-in-out shadow-2xl`}
      >
        <div className="flex h-full flex-col p-4">

          <div className="flex items-center justify-between mb-8 min-h-[40px]">
            <h2
              className={`text-xl sm:text-2xl font-extrabold tracking-tight bg-gradient-to-r from-blue-400 to-indigo-500 bg-clip-text text-transparent whitespace-nowrap transition-all duration-300 ${isCollapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100"
                }`}
            >
              TRANZACT
            </h2>

            <button
              onClick={toggleSidebar}
              className="p-3 rounded-lg hover:bg-gray-800 transition flex-shrink-0"
            >
              {isCollapsed ? (
                <ChevronDoubleRightIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              ) : (
                <ChevronDoubleLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              )}
            </button>
          </div>

          <div
            className={`bg-gray-800/60 rounded-xl border border-gray-700 transition-all duration-300 overflow-hidden ${isCollapsed ? "opacity-0 h-0 p-0 border-0 m-0" : "opacity-100 mb-8 p-4"
              }`}
          >
            <div className="font-medium text-lg whitespace-nowrap overflow-hidden text-ellipsis">
              {currentUser.fullName}
            </div>
            <div className="text-sm text-indigo-400 whitespace-nowrap overflow-hidden text-ellipsis">
              {currentUser.role}
            </div>
            <div className="text-xs text-gray-400 mt-2 font-mono whitespace-nowrap">
              {formatDateTime(currentTime)}
            </div>
          </div>

          <nav className="flex flex-col gap-2 flex-grow">

            {menuItems.filter(i => (!i.required) || ((i.required || []).includes(currentUser.systemRole))).map((item) => {
              const isActive = location.pathname === item.to;

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center ${isCollapsed ? "justify-center" : "gap-3"
                    } p-2 sm:px-3 sm:py-3 rounded-xl transition-all duration-200 ${isActive
                      ? "bg-indigo-600/30 text-white"
                      : "hover:bg-gray-800 text-gray-300 hover:text-white"
                    }`}
                >
                  <item.icon className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />

                  <span
                    className={`font-medium whitespace-nowrap transition-all duration-300 ${isCollapsed
                      ? "opacity-0 w-0 overflow-hidden"
                      : "opacity-100"
                      }`}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            })}

          </nav>

          <div
            className={`mt-auto pt-6 border-t border-gray-800 space-y-4 transition-all duration-300 overflow-hidden ${isCollapsed ? "opacity-0 h-0 pt-0 border-0" : "opacity-100"
              }`}
          >
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                <GlobeAltIcon className="h-4 w-4" />
                {t('language')}
              </label>

              <select
                value={i18n.language}
                onChange={changeLanguage}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm"
              >
                <option value="en">English</option>
                <option value="ru">Русский</option>
              </select>
            </div>

            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-red-700 hover:bg-red-600 transition"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              {t('logout')}
            </button>
          </div>

        </div>
      </aside>

      <div
        className={`flex-1 min-w-0 flex flex-col transition-[margin] duration-300 ${isCollapsed ? "ml-16 sm:ml-20" : "ml-16 sm:ml-72"
          }`}
      >
        <main className="flex-1 overflow-x-hidden overflow-y-auto p-2 sm:p-6">
          <Outlet />
        </main>
      </div>

    </div>
  );
}